import APPS from '@generio-ai/library-legacy/constants/apps'
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import CATEGORY from '@generio-ai/library-legacy/constants/category'
import ROUTES from '@generio-ai/library-legacy/constants/routes'
import { reactive } from 'vue'
import CARD from '@/constants/card.js'
import { Card } from '@/classes/card.js'
import { Prompt } from '@/classes/prompt.js'
import { Task } from '@/classes/task.js'
import { Transaction } from '@/classes/transaction.js'
import actions from './actions/agent.js'
import { AGENT_WAITING, agentMessages, nextAgentIndex, restoreAgentCards } from '@/utils/agent.js'

function releaseLocalPreviews(card) {
    for (const attachment of card.localAttachments || []) {
        if (attachment.preview?.startsWith('blob:')) URL.revokeObjectURL(attachment.preview)
    }
}

export default {
    namespaced: true,
    state: () => ({ initialized: false, busy: false, restoring: false, checking: false, expandedId: null, openedMessageId: null, error: '' }),
    mutations: {
        PATCH(state, patch) { Object.assign(state, patch) }
    },
    getters: {
        isBusy: state => !state.initialized || state.busy || state.restoring,
        cards: (state, getters, rootState, rootGetters) => rootGetters['flows/getCards'](APPS.AGENT),
        messages: (state, getters, rootState, rootGetters) => agentMessages(getters.cards, rootGetters['assets/getAsset']),
        expandedId: state => state.expandedId,
        openedMessageId: state => state.openedMessageId,
        error: state => state.error,
        isChecking: state => state.checking
    },
    actions: {
        ...actions,

        async init({ state, commit, dispatch, rootGetters }) {
            if (!rootGetters['flows/isRegistered'](APPS.AGENT)) {
                await dispatch('flows/registerApp', {
                    app: APPS.AGENT,
                    rootCard: new Card(null, 0, CARD.AGENT_INPUT, CATEGORY.PRIMITIVE, new Prompt())
                }, { root: true })
            }
            if (state.initialized || !rootGetters['assets/isSynchronized']) return
            commit('PATCH', { initialized: true })
        },

        async submitPrompt({ getters, commit, dispatch, rootGetters }, { text, attachments = [] }) {
            if (getters.isBusy || !text?.trim()) return false
            // Acquire the lock synchronously, before constructing or uploading anything.
            commit('PATCH', { busy: true, error: '', openedMessageId: null })
            const cards = getters.cards
            const context = [...cards].reverse().find(card => card.component === CARD.AGENT_MODEL && card.custom.agent.state === 'ready')
            const flow = rootGetters['flows/getFlowId'](APPS.AGENT)
            const input = cards.length === 1 && !cards[0].assets?.length
                ? cards[0]
                : reactive(new Card(flow, nextAgentIndex(cards), CARD.AGENT_INPUT, attachments.length ? CATEGORY.PHOTO : CATEGORY.PRIMITIVE, new Prompt()))
            input.flow = flow
            input.category = attachments.length ? CATEGORY.PHOTO : CATEGORY.PRIMITIVE
            input.primitive = !attachments.length
            input.prompt = new Prompt(text.trim())
            input.prompt.dual = !!context
            input.custom = { agent: { role: 'user', state: 'committing', imageCount: attachments.length, contextAssetId: context?.assets?.[0]?.id || null } }
            input.localFiles = attachments.map(attachment => attachment.file)
            input.localAttachments = attachments.map((attachment, index) => ({
                id: index,
                name: attachment.name,
                preview: URL.createObjectURL(attachment.file)
            }))
            if (!cards.includes(input)) commit('flows/ADD_CARD', { app: APPS.AGENT, card: input }, { root: true })
            dispatch('startResponse', { input }).catch(error => {
                commit('PATCH', { busy: false, error: error.message })
            })
            return true
        },

        async startResponse({ getters, commit, dispatch }, { input }) {
            const response = reactive(new Card(input.flow, nextAgentIndex(getters.cards), CARD.AGENT_MODEL, CATEGORY.MODEL, input.prompt.clone()))
            const requestId = crypto.randomUUID()
            response.custom = { agent: { role: 'agent', inputIndex: input.index, requestId, state: 'committing', status: 'Sending request' } }
            input.custom.agent.activeRequest = { id: requestId, index: response.index, state: 'committing' }
            commit('flows/ADD_CARD', { app: APPS.AGENT, card: response }, { root: true })
            return dispatch('runTransaction', { input, response })
        },

        async runTransaction({ commit, dispatch }, { input, response, resume = false }) {
            try {
                const taskCommit = resume ? new Task(resolve => resolve({ assets: input.assets })) : await dispatch('commitAgentInput', { input })
                const taskFetch = await dispatch('fetchAgent', { input, responseCard: response, resume })
                const transaction = new Transaction(APPS.AGENT, input, taskCommit, taskFetch)
                response.transaction = transaction
                transaction.onProgress((status, stage, assets) => {
                    if (assets) response.assets = assets
                    response.custom.agent.status = status
                    response.custom.agent.state = assets ? 'pending' : status === AGENT_WAITING ? 'requesting' : 'committing'
                })
                transaction.run()
                const result = await transaction.finished()
                response.assets = result.assets
                response.custom.agent = { ...response.custom.agent, state: 'ready', status: '', error: '' }
                input.custom.agent.activeRequest.state = 'ready'
                input.custom.agent.state = 'ready'
                input.localFiles = []
                input.uploads = null
                await dispatch('persistResponse', { input, response })
                commit('PATCH', { busy: false, checking: false })
            }
            catch (error) {
                response.custom.agent = {
                    ...response.custom.agent,
                    state: error.uncertain ? 'uncertain' : 'error',
                    error: error.message || 'The request failed. Please try again.'
                }
                input.custom.agent.activeRequest.state = response.custom.agent.state
                // An ambiguous POST or interrupted poll must not permit a second agent job.
                await dispatch('persistResponse', { input, response })
                commit('PATCH', { busy: !!error.uncertain, checking: false })
            }
        },

        async persistResponse({ dispatch }, { input, response }) {
            try {
                await dispatch('persistInput', { input })
                await Promise.all((response.assets || []).map(asset => dispatch('assets/updateAdditional', {
                    asset,
                    additional: { ...asset.additional, custom: { ...asset.additional?.custom, agent: { ...response.custom.agent } } }
                }, { root: true })))
            }
            catch { /* Server file status remains authoritative if a metadata write is interrupted. */ }
        },

        async retryResponse({ getters, commit, dispatch }, { id }) {
            if (getters.isBusy) return false
            const response = getters.cards.find(card => card.index === id && card.component === CARD.AGENT_MODEL)
            const input = getters.cards.find(card => card.index === response?.custom?.agent?.inputIndex)
            if (!input) return false
            if ((input.custom.agent.imageCount || 0) > (input.assets?.length || 0) && !input.localFiles?.length) {
                commit('PATCH', { error: 'Some reference images were not saved. Please attach them to a new request.' })
                return false
            }
            commit('PATCH', { busy: true, error: '' })
            await dispatch('startResponse', { input })
            return true
        },

        async checkStatus({ state, getters, commit, dispatch }, { id }) {
            if (state.checking || state.restoring) return
            const response = getters.cards.find(card => card.index === id && card.custom?.agent?.state === 'uncertain')
            if (!response) return
            const input = getters.cards.find(card => card.index === response.custom.agent.inputIndex)
            commit('PATCH', { checking: true, busy: true })
            if (!response.assets?.length) {
                try {
                    const result = await dispatch('api/assetsGet', undefined, { root: true })
                    commit('assets/UPDATE_OR_ADD', result.assets, { root: true })
                    const model = result.assets?.find(asset => asset.app === APPS.AGENT &&
                        asset.additional?.custom?.agent?.requestId === response.custom.agent.requestId)
                    if (!model) throw new Error('The request is not available yet. Check again before starting another conversation.')
                    response.assets = [model]
                }
                catch (error) {
                    response.custom.agent.error = error.message
                    commit('PATCH', { checking: false })
                    return
                }
            }
            await dispatch('runTransaction', { input, response, resume: true })
        },

        async newConversation({ getters, commit, dispatch }) {
            if (getters.isBusy) return false
            getters.cards.forEach(releaseLocalPreviews)
            await dispatch('flows/newFlow', { app: APPS.AGENT }, { root: true })
            const root = getters.cards[0]
            root.custom = {}
            root.prompt = new Prompt()
            root.localFiles = []
            root.localAttachments = []
            root.uploads = null
            commit('PATCH', { expandedId: null, openedMessageId: null, error: '' })
            return true
        },

        setExpanded({ getters, commit }, id) {
            if (id !== null) {
                const message = getters.messages.find(message => message.id === id && message.role === 'agent')
                const file = message?.asset?.files?.default
                if (message?.state !== 'ready' || !file?.data || file.state !== ASSETS.FILES.STATE.RETRIEVED) return false
            }
            commit('PATCH', { expandedId: id })
            return true
        },

        async openConversation({ state, getters, commit, dispatch, rootGetters }, { asset = null, flowId = asset?.additional?.flow, navigate = true }) {
            if (state.busy || state.restoring) {
                if (flowId !== rootGetters['flows/getFlowId'](APPS.AGENT)) {
                    commit('PATCH', { error: 'Wait for the current agent request to finish before opening another conversation.' })
                    return false
                }
                if (navigate) await dispatch('router/openRoute', { route: ROUTES.APPSUITE.AGENT }, { root: true })
                return true
            }
            const assets = rootGetters['assets/getAssets'].filter(item => item.app === APPS.AGENT && item.additional?.flow === flowId)
            if (!assets.length) return false
            commit('PATCH', { restoring: true, initialized: true, error: '', expandedId: null })
            try {
                getters.cards.forEach(releaseLocalPreviews)
                const cards = restoreAgentCards(assets, flowId)
                commit('flows/OPEN_FLOW', { app: APPS.AGENT, flowId, cards }, { root: true })
                const selected = cards.find(card => card.index === asset?.additional?.card?.index)
                commit('PATCH', { openedMessageId: selected?.index ?? null })
                if (navigate) await dispatch('router/openRoute', { route: ROUTES.APPSUITE.AGENT }, { root: true })

                for (const input of cards.filter(card => card.component === CARD.AGENT_INPUT)) {
                    const request = input.custom.agent?.activeRequest
                    if (!request || cards.some(card => card.index === request.index)) continue
                    const response = reactive(new Card(flowId, request.index, CARD.AGENT_MODEL, CATEGORY.MODEL, input.prompt.clone()))
                    response.custom = { agent: {
                        role: 'agent', inputIndex: input.index, requestId: request.id,
                        state: ['requesting', 'uncertain'].includes(request.state) ? 'uncertain' : 'error',
                        error: 'The previous request was interrupted. Check its status or try again.'
                    } }
                    commit('flows/ADD_CARD', { app: APPS.AGENT, card: response }, { root: true })
                }
                for (const response of [...getters.cards].filter(card => card.component === CARD.AGENT_MODEL)) {
                    const input = getters.cards.find(card => card.index === response.custom.agent.inputIndex)
                    if (response.custom.agent.state === 'uncertain') {
                        commit('PATCH', { busy: true })
                        continue
                    }
                    if (response.custom.agent.state === 'error') continue
                    if (response.custom.agent.state === 'ready') {
                        dispatch('refreshAgentModel', { model: response.assets[0], final: true }).catch(() => {})
                    }
                    else {
                        commit('PATCH', { busy: true })
                        await dispatch('runTransaction', { input, response, resume: true })
                    }
                }
                for (const input of getters.cards.filter(card => card.component === CARD.AGENT_INPUT)) {
                    for (const image of (input.assets || []).filter(item => item.type === ASSETS.TYPES.IMAGE)) {
                        dispatch('assets/fetchFile', { asset: image }, { root: true }).catch(() => {})
                    }
                }
                return true
            }
            catch (error) {
                commit('PATCH', { error: error.message })
                return false
            }
            finally { commit('PATCH', { restoring: false }) }
        }
    }
}
